import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

import { PageHeader } from "@/components/shared/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/useToast";

import type { TeamCalculationDocument } from "../../services/teamCalculationsService";
import {
  deleteTeamCalculation,
  listAllTeamCalculations,
  listTeamCalculations,
} from "../../services/teamCalculationsService";
import { formatCurrency, formatPercent } from "../../utils/formatting";
import { ActionMenu } from "../../components/ActionMenu/ActionMenu";

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "medium",
  timeStyle: "short",
});

export function SavedCalculationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const [myCalculations, setMyCalculations] = useState<TeamCalculationDocument[]>([]);
  const [allCalculations, setAllCalculations] = useState<TeamCalculationDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [removingCalculationId, setRemovingCalculationId] = useState<string | null>(null);
  const [showOnlyMine, setShowOnlyMine] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        const [mine, all] = await Promise.all([
          listTeamCalculations(user.email),
          listAllTeamCalculations(),
        ]);
        setMyCalculations(mine);
        setAllCalculations(all);
      } catch (error) {
        console.error("Erro ao carregar cálculos salvos:", error);
        toastError("Não foi possível carregar os cálculos salvos.");
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [toastError, user?.email]);

  const visibleCalculations = showOnlyMine ? myCalculations : allCalculations;
  const emptyState = !isLoading && visibleCalculations.length === 0;
  const handleOpenCalculation = useCallback(
    (calculationId: string) => {
      navigate(`/calculator/team?saved=${calculationId}`);
    },
    [navigate],
  );

  const handleRemoveCalculation = useCallback(
    async (calculationId: string) => {
      if (!user?.email) {
        toastError("Não foi possível identificar o usuário autenticado.");
        return;
      }

      const confirmRemoval = window.confirm("Tem certeza que deseja remover este cálculo?");
      if (!confirmRemoval) {
        return;
      }

      setRemovingCalculationId(calculationId);
      try {
        await deleteTeamCalculation(user.email, calculationId);
        setMyCalculations((prev) => prev.filter((calculation) => calculation.id !== calculationId));
        setAllCalculations((prev) => prev.filter((calculation) => calculation.id !== calculationId));
        toastSuccess("Cálculo removido com sucesso.");
      } catch (error) {
        console.error("Erro ao remover cálculo salvo:", error);
        toastError("Não foi possível remover o cálculo.");
      } finally {
        setRemovingCalculationId(null);
      }
    },
    [toastError, toastSuccess, user?.email],
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <PageHeader
          title="Meus cálculos"
          subtitle="Todos os cenários de time salvos ficam disponíveis aqui para consulta."
        />

        <div className="flex flex-wrap items-center gap-3">
          <ActionMenu variant="saved" />
          <button
            type="button"
            onClick={() => setShowOnlyMine((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
          >
            {showOnlyMine ? (
              <>
                <UsersIcon className="h-4 w-4" />
                Mostrar todos
              </>
            ) : (
              <>
                <UserIcon className="h-4 w-4" />
                Mostrar apenas meus
              </>
            )}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Carregando cálculos salvos...
        </div>
      ) : emptyState ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 p-6 text-center text-sm text-gray-500">
          Você ainda não salvou nenhum cálculo de time. Clique em "Criar novo cálculo" para começar.
        </div>
      ) : (
        <div className="space-y-4">
          {visibleCalculations.map((calculation) => (
            <SavedCalculationCard
              key={calculation.id}
              calculation={calculation}
              onOpen={handleOpenCalculation}
              onRemove={handleRemoveCalculation}
              isRemoving={removingCalculationId === calculation.id}
              showOwner={!showOnlyMine}
              currentUserEmail={user?.email ?? null}
              canRemove={calculation.createdBy === user?.email}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type SavedCalculationCardProps = {
  calculation: TeamCalculationDocument;
  onOpen: (calculationId: string) => void;
  onRemove: (calculationId: string) => void;
  isRemoving: boolean;
  showOwner: boolean;
  currentUserEmail: string | null;
  canRemove: boolean;
};

function SavedCalculationCard({
  calculation,
  onOpen,
  onRemove,
  isRemoving,
  showOwner,
  currentUserEmail,
  canRemove,
}: SavedCalculationCardProps) {
  const createdAtLabel = useMemo(() => {
    if (!calculation.createdAt?.toDate) {
      return "Data não disponível";
    }

    return dateFormatter.format(calculation.createdAt.toDate());
  }, [calculation.createdAt]);

  return (
    <article className="rounded-3xl border border-gray-100 bg-white p-5 shadow-lg shadow-gray-900/5 ring-1 ring-gray-900/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-500">
            {calculation.cards.length} {calculation.cards.length === 1 ? "profissional" : "profissionais"}
          </p>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">{calculation.title}</h3>
        </div>
        <div className="flex flex-col items-end gap-2 text-xs text-gray-500">
          {showOwner && (
            <OwnerBadge
              name={calculation.createdByName ?? calculation.createdBy}
              email={calculation.createdBy}
              avatarUrl={calculation.createdByAvatarUrl}
              highlight={calculation.createdBy === currentUserEmail}
            />
          )}
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4" />
            {createdAtLabel}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <SummaryItem
          label="Valor total de venda"
          value={formatCurrency(calculation.summary.totalSaleValue, { suffix: "/mês" })}
          accent="text-primary-600"
        />
        <SummaryItem
          label="Spread"
          value={`${formatPercent(calculation.summary.spreadPercentage)} · ${formatCurrency(calculation.summary.totalSpreadValue, { suffix: "/mês" })}`}
        />
        <SummaryItem
          label="Valor médio por recurso"
          value={formatCurrency(calculation.summary.averageSaleValue, { suffix: "/mês" })}
        />
        <SummaryItem
          label="Custos e impostos"
          value={`${formatCurrency(calculation.summary.totalMonthlyCost, { suffix: "/mês" })} · ${formatCurrency(calculation.summary.totalTaxes, { suffix: "/mês" })}`}
        />
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        {canRemove && (
          <IconButton
            label="Remover"
            variant="danger"
            icon={TrashIcon}
            onClick={() => onRemove(calculation.id)}
            disabled={isRemoving}
          />
        )}
        <IconButton
          label={canRemove ? "Abrir na calculadora" : "Ver"}
          variant="primary"
          icon={ArrowTopRightOnSquareIcon}
          onClick={() => onOpen(calculation.id)}
        />
      </div>
    </article>
  );
}

type SummaryItemProps = {
  label: string;
  value: string;
  accent?: string;
};

function SummaryItem({ label, value, accent }: SummaryItemProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-sm shadow-gray-900/5 ring-1 ring-gray-900/5">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-2 text-lg font-semibold ${accent ?? "text-gray-900"}`}>{value}</dd>
    </div>
  );
}

type IconButtonProps = {
  label: string;
  variant: "primary" | "danger";
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  disabled?: boolean;
};

function IconButton({ label, variant, icon: Icon, onClick, disabled }: IconButtonProps) {
  const classes =
    variant === "primary"
      ? "bg-primary-600 text-white hover:bg-primary-500"
      : "border border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-60 ${classes}`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

type OwnerBadgeProps = {
  name: string;
  email: string;
  avatarUrl?: string;
  highlight?: boolean;
};

function OwnerBadge({ name, email, avatarUrl, highlight }: OwnerBadgeProps) {
  const initials = useMemo(() => {
    if (name && name.trim()) {
      const parts = name.trim().split(" ");
      return parts.length === 1
        ? parts[0][0]?.toUpperCase() ?? "?"
        : `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return email[0]?.toUpperCase() ?? "?";
  }, [name, email]);

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
        highlight ? "border-primary-200 bg-primary-50 text-primary-700" : "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} className="h-6 w-6 rounded-full object-cover" />
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
          {initials}
        </span>
      )}
      <span>{name}</span>
    </div>
  );
}
